import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ModalButtonsContainer,
  REQUIRED_ERROR_MESSAGE,
  SelectInput,
  showDialog,
  showErrorDialog,
  showToast,
  TextButton,
  TextInput,
  type SelectOption,
} from "@SeemplicityDev/shared";
import {
  useCreateAnalystInstance,
  useCncAgentDefinitions,
  useUpdateAnalystInstance,
} from "../../../AnalystRuns/AnalystRunsApi";
import useLogger from "../../../Logger/useLogger";
import {
  buildDefaultValues,
  findAgentTypeOption,
  integrationSignature,
  normalizeName,
  optionToInstance,
  toAgentTypeSelectOptions,
  toIntegrationSelectOptions,
  type AnalystConfigFormData,
} from "./analystConfigFormUtils";
import {
  INTEGRATION_CATEGORY,
  type AddAnalystInstanceRequest,
  type AnalystInstanceBase,
  type AvailableIntegration,
  type IntegrationInstance,
  type UpdateAnalystInstanceRequest,
} from "../../../AnalystRuns/types";

interface AnalystConfigFormBaseProps {
  accountUuid: string;
  ticketProviderOptions: AvailableIntegration[];
  datasourceOptions: AvailableIntegration[];
  existingConfigs: AnalystInstanceBase[];
  integrationsLoading: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}
interface CreateFormProps extends AnalystConfigFormBaseProps {
  mode: "create";
  initialConfig?: undefined;
}
interface EditFormProps extends AnalystConfigFormBaseProps {
  mode: "edit";
  initialConfig: AnalystInstanceBase;
}
type AnalystConfigFormProps = CreateFormProps | EditFormProps;

const SUBMIT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const AnalystConfigForm: React.FC<AnalystConfigFormProps> = ({
  mode,
  accountUuid,
  ticketProviderOptions,
  datasourceOptions,
  existingConfigs,
  integrationsLoading,
  initialConfig,
  onCancel,
  onSuccess,
}) => {
  const isEdit = mode === "edit";

  const { logError } = useLogger();
  const createInstance = useCreateAnalystInstance(accountUuid);
  const updateInstance = useUpdateAnalystInstance(accountUuid);
  const { data: definitionsData, isLoading: isDefinitionsLoading } =
    useCncAgentDefinitions();
  const agentTypeOptions = useMemo(
    () => toAgentTypeSelectOptions(definitionsData?.agent_definitions ?? []),
    [definitionsData]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<AnalystConfigFormData>({
    mode: "all",
    defaultValues: buildDefaultValues(initialConfig),
  });

  const ticketProviderSelectOptions = toIntegrationSelectOptions(
    ticketProviderOptions
  );
  const datasourceSelectOptions = toIntegrationSelectOptions(datasourceOptions);

  const carriedInstances = useMemo<IntegrationInstance[]>(
    () =>
      (initialConfig?.integrations ?? [])
        .filter(
          (integration) =>
            integration.integration_category !== INTEGRATION_CATEGORY.TICKET &&
            integration.integration_category !== INTEGRATION_CATEGORY.DATASOURCE
        )
        .map((integration) => ({
          id: integration.id,
          integration_type: integration.integration_type,
          display_name: integration.display_name,
        })),
    [initialConfig]
  );

  const isRequestInFlight =
    isSubmitting || createInstance.isPending || updateInstance.isPending;

  const otherConfigs = existingConfigs.filter(
    (config) => config.agent_instance_id !== initialConfig?.agent_instance_id
  );

  const validateUniqueName = (value: string): string | true => {
    const name = value.trim();
    const clashes = otherConfigs.some(
      (config) => normalizeName(config.display_name) === normalizeName(name)
    );
    return clashes ? `A configuration named '${name}' already exists.` : true;
  };

  const assembleIntegrationInstances = (
    formData: AnalystConfigFormData
  ): IntegrationInstance[] => [
    ...(formData.ticketProvider
      ? [optionToInstance(formData.ticketProvider)]
      : []),
    ...formData.datasources.map(optionToInstance),
    ...carriedInstances,
  ];

  const findIdenticalConfig = (
    formData: AnalystConfigFormData,
    integrationInstances: IntegrationInstance[]
  ) => {
    const formSignature = integrationSignature(
      integrationInstances.map((row) => ({
        type: row.integration_type,
        display_name: row.display_name,
      }))
    );
    return otherConfigs.find(
      (config) =>
        normalizeName(config.display_name) !== normalizeName(formData.name) &&
        config.agent_type === formData.agent_type &&
        integrationSignature(
          config.integrations.map((integration) => ({
            type: integration.integration_type,
            display_name: integration.display_name,
          }))
        ) === formSignature
    );
  };

  const submitMutation = (
    formData: AnalystConfigFormData,
    integration_instances: IntegrationInstance[]
  ) => {
    if (isEdit && initialConfig) {
      const body: UpdateAnalystInstanceRequest = {
        name: formData.name,
        config: { integration_instances },
      };
      return updateInstance
        .mutateAsync({
          agentInstanceId: initialConfig.agent_instance_id,
          body,
        })
        .then(() => {
          showToast({ message: "Config updated" });
          onSuccess();
        })
        .catch((err: unknown) => {
          logError(err, "Failed to update analyst config");
          showErrorDialog({ message: SUBMIT_ERROR_MESSAGE });
        });
    }

    const body: AddAnalystInstanceRequest = {
      agent_type: formData.agent_type as string,
      name: formData.name,
      config: { integration_instances },
    };
    return createInstance
      .mutateAsync(body)
      .then(() => {
        showToast({ message: "Config created" });
        onSuccess();
      })
      .catch((err: unknown) => {
        logError(err, "Failed to create analyst config");
        showErrorDialog({ message: SUBMIT_ERROR_MESSAGE });
      });
  };

  const onSubmit = (formData: AnalystConfigFormData) => {
    const integration_instances = assembleIntegrationInstances(formData);
    const identicalConfig = findIdenticalConfig(
      formData,
      integration_instances
    );
    if (identicalConfig) {
      showDialog({
        title: isEdit ? "Save Config" : "Create Config",
        message: `A configuration with identical integrations already exists ('${identicalConfig.display_name}'). ${
          isEdit ? "Save anyway?" : "Create another anyway?"
        }`,
        buttons: [
          {
            "data-testid": "account-configs-duplicate-cancel",
            label: "Cancel",
          },
          {
            "data-testid": "account-configs-duplicate-confirm",
            buttonStyle: "primary",
            label: isEdit ? "Save Anyway" : "Create Anyway",
            onClick: () => {
              submitMutation(formData, integration_instances);
            },
          },
        ],
      });
      return;
    }
    return submitMutation(formData, integration_instances);
  };

  return (
    <form className="account-configs-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="modal-scroll-area">
        <Controller
          name="agent_type"
          control={control}
          rules={isEdit ? undefined : { required: REQUIRED_ERROR_MESSAGE }}
          render={({ field: { value, onChange, onBlur } }) => (
            <SelectInput
              name="agent_type"
              data-testid="account-configs-agent-type"
              labelTop
              label="Agent Type"
              placeholder="Select Agent Type"
              options={agentTypeOptions}
              isLoading={isDefinitionsLoading}
              value={findAgentTypeOption(agentTypeOptions, value)}
              onChange={(selected: SelectOption | null) =>
                onChange(selected?.value ?? null)
              }
              onBlur={onBlur}
              disabled={isEdit}
              error={errors.agent_type}
              required={!isEdit}
            />
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
            validate: validateUniqueName,
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextInput
              name="name"
              data-testid="account-configs-name"
              labelTop
              label="Name"
              placeholder="Config name"
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={errors.name ? errors.name : null}
              required
            />
          )}
        />

        <div className="account-configs-integrations">
          <Controller
            name="datasources"
            control={control}
            rules={{
              validate: (v: SelectOption[]) =>
                (v?.length ?? 0) > 0 || "Select at least one data source",
            }}
            render={({ field: { value, onChange, onBlur } }) => (
              <SelectInput
                name="data-sources"
                data-testid="account-configs-datasources"
                labelTop
                label="Data sources"
                placeholder="Select Data sources"
                isMulti
                options={datasourceSelectOptions}
                disabled={integrationsLoading}
                setDefaultSingleOption={false}
                required
                value={value}
                onChange={(selected: SelectOption[] | null) =>
                  onChange(selected ?? [])
                }
                onBlur={onBlur}
              />
            )}
          />
          <Controller
            name="ticketProvider"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <SelectInput
                name="integrations"
                data-testid="account-configs-ticket-providers"
                labelTop
                label="Ticket Providers"
                placeholder="Select Ticket Provider"
                options={ticketProviderSelectOptions}
                disabled={integrationsLoading}
                setDefaultSingleOption={false}
                isClearable
                value={value}
                onChange={(selected: SelectOption | null) =>
                  onChange(selected ?? null)
                }
                onBlur={onBlur}
              />
            )}
          />
        </div>
      </div>

      <ModalButtonsContainer spaceBetween>
        <TextButton
          type="button"
          data-testid="account-configs-back"
          label="< Back to list"
          disabled={isRequestInFlight}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onCancel();
          }}
        />
        <TextButton
          type="submit"
          data-testid="account-configs-form-submit"
          label={isEdit ? "Save Changes" : "Create"}
          buttonStyle="primary"
          isSubmitting={isRequestInFlight}
          rightIcon={isRequestInFlight ? "seem-icon-spinner spinner" : ""}
          disabled={!isValid || isRequestInFlight || (isEdit && !isDirty)}
        />
      </ModalButtonsContainer>
    </form>
  );
};

export default AnalystConfigForm;
